import {
  selectFinancialYear,
  selectPartnerAccess,
  selectPartnerGeoMarket,
  selectPartnerId,
  selectProgramTypesExtended,
} from 'legacy/lut/features/partner360/configuration'
import { selectPartner360Data } from 'legacy/lut/features/partner360/configuration/selectors/selectPartner360Data'
import { selectSectionSettings } from 'legacy/lut/features/partner360/sectionSettings'
import { fetchPartner360Configuration } from 'legacy/lut/services/partner360/configuration/fetchPartner360Configuration'
import { map621ToConfigurationData } from 'legacy/lut/services/partner360/configuration/mappings/map621ToConfigurationData'
import { fetchSectionSettings } from 'legacy/lut/services/partner360/sectionSettings/fetchSectionSettings'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { selectAppOwnerId } from 'core-components/sitebuilder/store/selectors/selectAppOwnerId'
import { getOfferingActualsValueTypeDefault } from 'partner360/helpers/getOfferingActualsValueTypeDefault'

import type { TPlanReviewOffering } from './types'

/** What the plan's quality is measured against that belongs to the partner, not to the plan. */
export interface IPlanReviewPartnerSettings {
  partnerFlags?: string
  geoMarket: ReturnType<typeof selectPartnerGeoMarket>
  isRegionalDistributor: boolean
  actualsType: ReturnType<typeof getOfferingActualsValueTypeDefault>
  sectionSettings: ReturnType<typeof selectSectionSettings>
  isDistributor: boolean
  offerings: ReturnType<typeof selectProgramTypesExtended>
  isConfigurationAvailable: boolean
}

interface IUsePlanReviewPartnerSettingsParams {
  partnerId: string
  fYear: number
  programType: TPlanReviewOffering
}

/**
 * The partner's settings for the review. On the partner's own page they are the page's; anywhere
 * else — the manager cockpit stands on no partner, or on another one — they are read for this
 * partner alone, so the page's store is never touched and never read in its place.
 */
export const usePlanReviewPartnerSettings = ({
  partnerId,
  fYear,
  programType,
}: IUsePlanReviewPartnerSettingsParams) => {
  const { partnerFlags = '' } = useSelector(selectPartner360Data)
  const pagePartnerId = useSelector(selectPartnerId)
  const pageFYear = useSelector(selectFinancialYear)
  const pageGeoMarket = useSelector(selectPartnerGeoMarket)
  const { isRegionalDistributor: pageIsRegionalDistributor, isDistributor: pageIsDistributor } =
    useSelector(selectPartnerAccess)
  const pageOfferings = useSelector(selectProgramTypesExtended)
  const pageSectionSettings = useSelector(selectSectionSettings)
  const ownerId = useSelector(selectAppOwnerId)

  const isPagePartner = Boolean(partnerId) && pagePartnerId === partnerId && pageFYear === fYear

  const pageSettings = useMemo<IPlanReviewPartnerSettings>(
    () => ({
      partnerFlags,
      geoMarket: pageGeoMarket,
      isRegionalDistributor: pageIsRegionalDistributor,
      actualsType: getOfferingActualsValueTypeDefault(
        pageOfferings.find((item) => item.Offering === programType)
      ),
      sectionSettings: pageSectionSettings,
      isDistributor: pageIsDistributor,
      offerings: pageOfferings,
      isConfigurationAvailable: true,
    }),
    [
      partnerFlags,
      programType,
      pageGeoMarket,
      pageIsRegionalDistributor,
      pageSectionSettings,
      pageIsDistributor,
      pageOfferings,
    ]
  )

  const [ownSettings, setOwnSettings] = useState<{
    partnerId: string
    fYear: number
    programType: TPlanReviewOffering
    settings: IPlanReviewPartnerSettings
  } | null>(null)

  useEffect(() => {
    if (isPagePartner || !partnerId || !fYear) {
      return
    }

    let isCurrent = true
    setOwnSettings(null)

    /* A failed read leaves the partner at the defaults rather than the review stuck loading. */
    Promise.all([
      fetchPartner360Configuration({ partnerId, fYear })
        .then((data) => map621ToConfigurationData(data, programType ?? undefined))
        .catch(() => null),
      fetchSectionSettings({ partnerId, fYear, ownerId }).catch(() => ({})),
    ]).then(([partnerData, sectionSettings]) => {
      if (!isCurrent) {
        return
      }

      setOwnSettings({
        partnerId,
        fYear,
        programType,
        settings: {
          partnerFlags: partnerData?.partnerFlags ?? '',
          geoMarket: partnerData?.geoMarket ?? null,
          isRegionalDistributor: partnerData?.partnerAccess.isRegionalDistributor ?? false,
          actualsType: partnerData?.actualsData.valueTypeDefault ?? pageSettings.actualsType,
          sectionSettings: sectionSettings as IPlanReviewPartnerSettings['sectionSettings'],
          isDistributor: partnerData?.partnerAccess.isDistributor ?? false,
          offerings: partnerData?.programTypesExtended ?? [],
          isConfigurationAvailable: partnerData !== null,
        },
      })
    })

    return () => {
      isCurrent = false
    }
    // Page settings are only a failed read's fallback, not a reason to read again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fYear, isPagePartner, ownerId, partnerId, programType])

  if (isPagePartner) {
    return { settings: pageSettings, isLoading: false }
  }

  const matchingSettings =
    ownSettings?.partnerId === partnerId &&
    ownSettings.fYear === fYear &&
    ownSettings.programType === programType
      ? ownSettings.settings
      : null

  return {
    settings: matchingSettings ?? pageSettings,
    isLoading: Boolean(partnerId && fYear) && matchingSettings === null,
  }
}
